<template>
  <v-container id="query-issue" fluid class="pa-0 ma-0">
    <div>
      <v-row class="p-top-row">
        <v-col cols="12" md="3" class="px-2 px-md-8">
          <p class="g-bold mb-4">1. Issue Type</p>
          <span>{{ issue.queryType }}</span>
        </v-col>

        <v-col cols="12" md="3" class="px-2 px-md-8">
          <p class="g-bold mb-4">2. Quantity Details</p>
          <template v-if="isVariableWeight">
            <v-row>
              <v-col cols="6">
                <span>Revised Weight (KG)</span>
              </v-col>
              <v-col cols="6">
                <span>{{ issue.variableWeightInKg }}</span>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <span>Credit Weight (KG)</span>
              </v-col>
              <v-col cols="6">
                <span>{{ issue.disputedWeightInKg }}</span>
              </v-col>
            </v-row>
            <v-row class="mb-2">
              <v-col cols="6">
                <span>Price per KG{{ incTaxSuffix }}</span>
              </v-col>
              <v-col cols="6">
                <span>{{
                  $filters.currency(
                    displayAmount(issue.priceKgExGst, issue.priceKgIncGst)
                  )
                }}</span>
              </v-col>
            </v-row>
          </template>

          <template v-else>
            <v-row>
              <v-col cols="6">
                <span>Revised Quantity</span>
              </v-col>
              <v-col cols="6">
                <span>{{ issue.quantity }}</span>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <span>Credit Quantity</span>
              </v-col>
              <v-col cols="6">
                <span>{{ issue.disputedQuantity }}</span>
              </v-col>
            </v-row>
            <v-row class="mb-2">
              <v-col cols="6">
                <span>Carton Price{{ incTaxSuffix }}</span>
              </v-col>
              <v-col cols="6">
                <span>{{
                  $filters.currency(
                    displayAmount(issue.priceUnitExGst, issue.priceUnitIncGst)
                  )
                }}</span>
              </v-col>
            </v-row>
          </template>

          <v-divider class="my-2"></v-divider>

          <v-row>
            <v-col cols="6">
              <strong>Product Credit<br />{{ incTaxText }}</strong>
            </v-col>
            <v-col cols="6">
              <i-query-issue-price
                :issue="issue.queryIssue"
                :is-variable-weight="isVariableWeight"
              />
            </v-col>
            <template v-if="!isVariableWeight">
              <v-col cols="6" class="pt-0">
                <strong>Freight<br />{{ incTaxText }}</strong>
              </v-col>
              <v-col cols="6" class="pt-0">
                <span
                  ><strong>{{
                    $filters.currency(
                      displayAmount(
                        issue.queryIssue.requestedCreditAmountDeliveryExTax ||
                          0,
                        issue.queryIssue.requestedCreditAmountDeliveryIncTax ||
                          0
                      )
                    )
                  }}</strong></span
                >
              </v-col>
            </template>
          </v-row>

          <v-divider class="my-2"></v-divider>

          <v-row>
            <v-col cols="6">
              <span
                ><strong>Credit Amount<br />{{ incTaxText }}</strong></span
              >
            </v-col>
            <v-col cols="6">
              <span
                ><strong>{{
                  $filters.currency(
                    displayAmount(
                      issue.queryIssue.requestedCreditAmountExTax,
                      issue.queryIssue.requestedCreditAmountIncTax
                    )
                  )
                }}</strong></span
              >
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12" md="3" class="px-2 px-md-8">
          <p class="g-bold mb-4">3. Photos of Product</p>
          <v-row v-if="hasImage(0)">
            <v-col cols="12" class="pt-0">
              <v-btn
                class="img-btn"
                @click="openImage(0)"
                :disabled="!hasWebReadyImage(0)"
              >
                <v-icon left class="mt-1">mdi-camera-outline</v-icon>
                <span>Image 1</span>
              </v-btn>
            </v-col>
            <v-col cols="12" class="pt-4" v-if="hasImage(1)">
              <v-btn
                class="img-btn"
                @click="openImage(1)"
                :disabled="!hasWebReadyImage(1)"
              >
                <v-icon left class="mt-1">mdi-camera-outline</v-icon>
                <span>Image 2</span>
              </v-btn>
            </v-col>
            <v-col cols="12" class="pt-4" v-if="hasImage(2)">
              <v-btn
                class="img-btn"
                @click="openImage(2)"
                :disabled="!hasWebReadyImage(2)"
              >
                <v-icon left class="mt-1">mdi-camera-outline</v-icon>
                <span>Image 3</span>
              </v-btn>
            </v-col>
          </v-row>
          <p v-else>No photos provided</p>
        </v-col>

        <v-col cols="12" md="3" class="px-2 px-md-8">
          <p class="g-bold mb-4">4. Additional Comment</p>
          <p>{{ issue.queryIssue.comment }}</p>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { openFile } from '@/utils/common'
import iQueryIssuePrice from '@/modules/orderQueries/components/QueryIssuePrice'
import { mapGetters } from 'vuex'
import { pickSupplierAmount } from '@/utils/supplierAmountDisplay'

export default {
  components: {
    iQueryIssuePrice
  },

  props: {
    issue: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    issueOptions: [
      { value: 'INCORRECT_PRODUCT', text: 'Incorrect Product' },
      { value: 'DAMAGED', text: 'Damaged Product' },
      {
        value: 'MISSING_QUANTITY',
        text: 'Missing / Short Quantity',
        weightType: 'FIXED_WEIGHT'
      },
      {
        value: 'MISSING_QUANTITY',
        text: 'Missing / Short Weight (KG)',
        weightType: 'VARIABLE_WEIGHT'
      },
      { value: 'OTHER', text: 'Other' }
    ]
  }),

  computed: {
    ...mapGetters('tenant', ['tenantCountryRegion']),
    filteredIssueOptions() {
      return this.isVariableWeight
        ? this.issueOptions.filter(
            (issue) => !(issue.weightType === 'FIXED_WEIGHT')
          )
        : this.issueOptions.filter(
            (issue) => !(issue.weightType === 'VARIABLE_WEIGHT')
          )
    },

    isVariableWeight() {
      return this.issue.weightType === 'VARIABLE_WEIGHT'
    },

    isUSCountry() {
      return this.tenantCountryRegion === 'US'
    },

    incTaxSuffix() {
      return this.isUSCountry ? '' : ' (inc Tax)'
    },

    incTaxText() {
      return this.isUSCountry ? '' : '(inc Tax)'
    }
  },

  methods: {
    displayAmount(exTax, incTax) {
      return pickSupplierAmount({
        tenantCountryRegion: this.tenantCountryRegion,
        exTax,
        incTax
      })
    },

    openImage(position) {
      openFile(this.issue.queryIssue.images[position]?.imageUrl)
    },

    hasImage(position) {
      return !!this.issue.queryIssue.images[position]?.imageUrl
    },

    hasWebReadyImage(position) {
      if (!this.hasImage(position)) return false
      const imageUrl = new URL(this.issue.queryIssue.images[position]?.imageUrl)
      // A newly uploaded HEIC file may not have had time to be converted to a web-ready version just yet!
      return !imageUrl.pathname.toLowerCase()?.endsWith('heic') || false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#query-issue {
  .p-top-row {
    min-height: 250px;
  }
  .img-btn {
    text-transform: none !important;
    font-weight: bold;
    border-radius: 100px;
    min-width: 150px;
    background-color: $primary-color;
    color: white;
  }
  .img-btn span {
    font-size: 14px;
    letter-spacing: 0.5px;
  }
}
</style>
